export interface FileUploadProgress {
  phase:'uploading'|'saving';
  uploadedBytes:number;
  totalBytes:number;
  bytesPerSecond:number|null;
  determinate:boolean;
}
export interface UploadProgress extends Omit<FileUploadProgress,'phase'> {
  phase:'checking'|'uploading'|'saving';
  fileName:string;
  fileIndex:number;
  fileCount:number;
}
export type UploadProgressHandler=(progress:UploadProgress)=>void;

/** Cancellation is successful only after the server confirms its cleanup boundary. */
export class UploadCancelledError extends Error {
  constructor(public completedCount=0){super('上传已终止。');this.name='UploadCancelledError';}
}
export class UploadCleanupError extends Error {
  constructor(public retry:()=>Promise<UploadCancelledError>){
    super('已停止发送，但暂时无法确认未完成文件已清理，请重试清理。');this.name='UploadCleanupError';
  }
}
export const uploadCancelledMessage=(completedCount:number)=>`已终止上传，未完成文件已清理。${completedCount>0?`已完成的 ${completedCount} 个文件已保留。`:''}`;

/** Payload progress uses the browser's multipart fraction, excluding form overhead. */
export function uploadedFileBytes(size:number,loaded:number,total:number):number {
  if(!Number.isFinite(loaded)||!Number.isFinite(total)||total<=0)return 0;
  return Math.floor(size*Math.min(1,Math.max(0,loaded/total)));
}

/** A two-second moving rate; periodic zero-delta samples let stalled transfers reach 0. */
export function uploadRate(startedAt:number) {
  const samples=[{at:startedAt,bytes:0}];
  let lastBytes=0;
  return (bytes:number,at:number):number|null=>{
    lastBytes=Math.max(lastBytes,Number.isFinite(bytes)?bytes:0);
    const previous=samples.at(-1)!;
    if(at>previous.at)samples.push({at,bytes:lastBytes});
    else previous.bytes=lastBytes;
    while(samples.length>1&&samples[1]!.at<=at-2000)samples.shift();
    const first=samples[0]!,elapsed=at-first.at;
    if(elapsed<=0||(lastBytes===0&&at-startedAt<1000))return null;
    return Math.max(0,(lastBytes-first.bytes)*1000/elapsed);
  };
}

const units=['B','KB','MB','GB','TB'];
const unitFor=(bytes:number)=>Math.min(units.length-1,Math.max(0,Math.floor(Math.log10(Math.max(1,bytes))/3)));
const amount=(bytes:number,unit:number)=>`${(Math.max(0,bytes)/1000**unit).toLocaleString('en-US',{maximumFractionDigits:unit===0?0:1})} ${units[unit]}`;
export function uploadSizePair(uploaded:number,total:number):string {
  const unit=unitFor(total);
  return `${amount(Math.min(uploaded,total),unit)} / ${amount(total,unit)}`;
}
export function uploadSpeed(bytesPerSecond:number|null):string {
  return bytesPerSecond===null?'计算速度…':`${amount(bytesPerSecond,unitFor(bytesPerSecond))}/s`;
}
